import { DragEndEvent } from "@dnd-kit/core";
import { Col, Container, Offcanvas, Row } from "react-bootstrap";
import { Control210, Control211, SortableListHolder } from "../../controls";
import { EditorProps, IValidationFunction, IValidReturnSuccess } from "../../utility";
import { CaseWhenControl, WhereClauseInput } from "../components";
import { ArsTextJustification } from "../table";
import * as styles from "./ArsListing.module.css";
import { ArsListingColumnDropSortVariableTarget } from "./ArsListingColumnDropSortVariableTarget";
import { ArsListingColumnNavButtons } from "./ArsListingColumnNavButtons";
import { ArsListingColumnVariableProperties } from "./ArsListingColumnVariableProperties";
import { useArsListing } from "./ArsListingContextProvider";
import { ARSL_COLUMN_UPDATE, ARSL_SET_VIEW_COLUMN_PROPERTIES_ID } from "./interface";

/**
 * Show column listing properties sidebar
 *
 * @returns Listing properties sidebar
 */
export const ArsListingColumnProperties = <T,>() => {
  const { state, dispatch } = useArsListing<T>();
  if (!state?.settings.viewColumnPropertiesId) return null;
  const colId = state?.settings.viewColumnPropertiesId;
  const colIx = state.bodyData.arsListingColumns.findIndex((col) => col.id === colId);
  const column = colIx >= 0 ? state.bodyData.arsListingColumns[colIx] : undefined;
  if (!column) return null;

  /**
   * Update component state
   */
  const handleHide = () =>
    dispatch({ operation: ARSL_SET_VIEW_COLUMN_PROPERTIES_ID, colId: undefined });

  const value = state.settings.editorProps.joinTintoBlock(column.displayPattern);

  const alignOptions = Object.entries(ArsTextJustification).map(([k, v]) => ({
    label: k,
    value: v,
  }));

  const Editor = state.settings.editorProps.Editor;
  const editorProps: Partial<EditorProps<T>> = {
    className: `${styles.arsListingColumnPropertiesEditor}`,
    disabled: state.settings.disabled,
    editable: true,
    resize: "vertical",
  };

  /**
   * Handle variable reordering
   *
   * @param event - Sortable list drag end event
   */
  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    /* istanbul ignore else */
    if (column && over !== null && over.id !== active.id) {
      const oldIndex = column.variables.findIndex((v) => `${v.id}` === active.id);
      const newIndex = column.variables.findIndex((v) => `${v.id}` === over.id);

      // Reorder the array
      const reorderedVariables = [...column.variables];
      const [movedVariable] = reorderedVariables.splice(oldIndex, 1);
      reorderedVariables.splice(newIndex, 0, movedVariable);

      dispatch({
        operation: ARSL_COLUMN_UPDATE,
        colId,
        column: { variables: reorderedVariables },
      });
    }
  };

  /**
   * Validate that all variables in the result pattern are present
   * in the column's variables.
   *
   * @param _backEndUrl - Backend URL (not used in this validation)
   * @param value - The value to validate
   * @param _controller - Abort controller (not used in this validation)
   * @returns A promise that resolves to an IValidReturn object indicating whether the validation passed or failed
   */
  const validateResultPattern: IValidationFunction = (_backEndUrl, value, _controller) => {
    /* Use regex to find all variable references between curly braces */
    const variablePattern = /\{([^}]+)\}/g;
    const matches = value.value.match(variablePattern);
    if (!matches) {
      const ret: IValidReturnSuccess = { success: true, data: { valid: true, validationText: "" } };
      return Promise.resolve(ret);
    }
    const variableNamesInPattern = matches.map((match) => match.slice(1, -1));

    /* Get the variable names from the column's variables */
    const variableNamesInColumn = column.variables.map((v) => v.variable.toLowerCase());

    /* Check if all variables in the pattern are present in the column's variables */
    const missingVariables = variableNamesInPattern.filter(
      (varName) => !variableNamesInColumn.includes(varName.toLowerCase()),
    );
    /* It's not good */
    if (missingVariables.length > 0) {
      const ret: IValidReturnSuccess = {
        success: true,
        data: { valid: false, validationText: `Missing variables: ${missingVariables.join(", ")}` },
      };
      return Promise.resolve(ret);
    }
    /* Everything is good */
    const ret: IValidReturnSuccess = { success: true, data: { valid: true, validationText: "" } };
    return Promise.resolve(ret);
  };

  return (
    <Offcanvas
      id={"ars-listing-column-properties"}
      show={column !== undefined}
      onHide={handleHide}
      backdrop={false}
      scroll={true}
    >
      <Offcanvas.Header closeButton>
        <Offcanvas.Title className="d-flex flex-row justify-content-between w-100">
          <span className="d-flex flex-row align-items-left gap-1">
            Column{" "}
            {colIx !== undefined
              ? colIx + 1
              : // istanbul ignore next
                "?"}
          </span>
          <ArsListingColumnNavButtons colIx={colIx} />
        </Offcanvas.Title>
      </Offcanvas.Header>
      <Offcanvas.Body>
        <Container>
          <small>Id: {colId}</small>
          <Control210
            id={"ars-listing-column-properties-column-width"}
            disabled
            label={"Column width"}
            labelWidth={3}
            value={`${column.columnWidth}`}
          />
          <Control211
            id={"ars-listing-column-properties-body-text-alignment"}
            disabled={state.settings.disabled}
            label={"Body text alignment"}
            labelWidth={3}
            options={alignOptions}
            setValue={(ret) =>
              dispatch({
                operation: ARSL_COLUMN_UPDATE,
                colId,
                column: { bodyTextAlignment: ret as ArsTextJustification },
              })
            }
            value={column.bodyTextAlignment ?? "left"}
          />
          <Control210
            id={"ars-listing-column-properties-decimal-align-percent"}
            disabled={state.settings.disabled}
            label={"Decimal align %"}
            labelWidth={3}
            setValue={(ret) => {
              const parsed = parseFloat(ret);
              const parsedValue: number | undefined = isNaN(parsed)
                ? // istanbul ignore next
                  undefined
                : parsed;
              dispatch({
                operation: ARSL_COLUMN_UPDATE,
                colId,
                column: { decimalAlignPercent: parsedValue },
              });
            }}
            type="number"
            value={`${column.decimalAlignPercent ?? ""}`}
          />
          <Row className="my-1">
            <Col
              md={3}
              id={"ars-listing-column-properties-display-pattern-label"}
              htmlFor={"ars-listing-column-properties-display-pattern"}
              className="form-label col-form-label"
            >
              Display value
            </Col>
            <Col>
              <Editor
                {...editorProps}
                id={"ars-listing-column-properties-display-pattern"}
                aria-labelledby={"ars-listing-column-properties-display-pattern-label"}
                decimalAlignPercent={column.decimalAlignPercent}
                setValue={(ret) => {
                  const newValue: T[] = state.settings.editorProps.splitTintoLines(ret);
                  dispatch({
                    operation: ARSL_COLUMN_UPDATE,
                    colId,
                    column: { displayPattern: newValue },
                  });
                }}
                textAlignment={column.bodyTextAlignment ?? "left"}
                value={value}
              />
            </Col>
          </Row>
          <Row className="my-1">
            <Col
              className="form-label col-form-label"
              htmlFor={"ars-listing-column-properties-header-text"}
              id={"ars-listing-column-properties-header-text-label"}
              md={3}
            >
              Header text
            </Col>
            <Col>
              <Editor
                {...editorProps}
                id="ars-listing-column-properties-header-text"
                aria-labelledby="ars-listing-column-properties-header-text-label"
                decimalAlignPercent={column.decimalAlignPercent}
                setValue={(ret) => {
                  dispatch({ operation: ARSL_COLUMN_UPDATE, colId, column: { headerText: ret } });
                }}
                textAlignment={column.headerTextAlignment ?? "center"}
                value={column.headerText ?? state.settings.editorProps.blankT}
              />
            </Col>
          </Row>
          <Control210
            id={"ars-listing-column-properties-header-height"}
            disabled
            label={"Header height"}
            labelWidth={3}
            value={`${column.headerHeight ?? ""}`}
          />
          <Control211
            id="ars-listing-column-properties-header-text-alignment"
            disabled={state.settings.disabled}
            label="Header text alignment"
            labelWidth={3}
            options={alignOptions.filter((option) => option.value !== ArsTextJustification.Decimal)}
            setValue={(ret) =>
              dispatch({
                operation: ARSL_COLUMN_UPDATE,
                colId,
                column: { headerTextAlignment: ret as ArsTextJustification },
              })
            }
            value={column.headerTextAlignment ?? "left"}
          />
          <Control210
            id="ars-listing-column-properties-result-pattern"
            disabled={state.settings.disabled}
            label="Result pattern"
            labelWidth={3}
            setValue={(ret) => {
              dispatch({
                operation: ARSL_COLUMN_UPDATE,
                colId,
                column: { resultPattern: ret },
              });
            }}
            validCondition={validateResultPattern}
            value={column.resultPattern}
          />
          <Row className="my-1">
            <Col
              id="ars-listing-column-properties-variables-label"
              aria-label="Variables associated with the column"
              className="mt-3 form-label col-form-label"
              htmlFor="ars-listing-column-properties-variables"
            >
              <span>Variables</span>
            </Col>
          </Row>
          <Row>
            <Col className={styles.columnVariablesHolder}>
              <SortableListHolder
                id="ars-listing-column-properties-variables"
                aria-labelledby="ars-listing-column-properties-variables-label"
                canMove={!state.settings.disabled}
                handleDragEnd={handleDragEnd}
                items={column.variables.map((v) => ({
                  id: v.id,
                  label: v.dataset,
                  element: <ArsListingColumnVariableProperties id={v.id} />,
                }))}
              />
              <ArsListingColumnDropSortVariableTarget
                colId={column.id}
                addVarIx={column.variables.length}
              />
              <small className="ms-2">Variables dropped here will be added to the sort order</small>
            </Col>
          </Row>
          <Row className="my-1">
            <Col
              id="ars-listing-column-properties-case-when-label"
              aria-label="Complex display conditions"
              className="mt-3 form-label col-form-label"
              htmlFor="ars-listing-column-properties-case-when"
            >
              <span>Complex display conditions</span>
            </Col>
          </Row>
          <Row>
            <Col>
              <CaseWhenControl<T>
                id="ars-listing-column-properties-case-when"
                aria-labelledby="ars-listing-column-properties-case-when-label"
                dataPath={`/caseWhenConditions/${column.id}`}
                disabled={state.settings.disabled}
                editorDefns={state.settings.editorProps}
                onChange={(ret) => {
                  // Update the ARS listing state to show a change has been made
                  dispatch({
                    operation: ARSL_COLUMN_UPDATE,
                    colId,
                    column: { caseWhenCondition: ret },
                  });
                }}
              />
            </Col>
          </Row>
          <Row className="my-1">
            <Col
              id="ars-listing-column-properties-filter-where-clause-label"
              aria-label="Filter where clause"
              className="mt-3 form-label col-form-label"
              htmlFor="ars-listing-column-properties-filter-where-clause"
            >
              <span>Filter where clause</span>
            </Col>
          </Row>
          <Row>
            <Col>
              <WhereClauseInput
                dataPath={`/arsListingColumns/${column.id}/filterWhereClause`}
                disabled={state.settings.disabled}
                onChange={(ret) => {
                  dispatch({
                    operation: ARSL_COLUMN_UPDATE,
                    colId,
                    column: { filterWhereClause: ret ?? undefined },
                  });
                }}
                value={column.filterWhereClause}
              />
            </Col>
          </Row>
        </Container>
      </Offcanvas.Body>
    </Offcanvas>
  );
};

ArsListingColumnProperties.displayName = "ArsListingColumnProperties";
